import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import config from '../../config';
import { getAccessToken, getChoreographySubjectToken } from '../../services/authStorage';
import { ensureChoreographySubjectToken } from '../../services/choreographyAuth';
import { choreographyApi } from '../../services/api';
import {
  COLLABORATION_PROTOCOL, applyOperation, canvasScore, collaborationAnchorRedirects, containsServerOnlyCompound, exactSource, getClientInstanceId, inverseOperations, lineEditOperations,
  makeEnvelope, orderedLines, transformCollaborationAnchor, transformCollaborationAnchorWithRedirects, transformPendingThroughRemote, transformThroughRemote, validateBootstrap,
  verifyDocumentHash,
} from './collaborationProtocol';
import {
  collaborationQueueProtection, collaborationStorageIncident, discardCollaborationQueue, loadCollaborationQueue, putCollaborationQueueItem,
  removeCollaborationQueueItem, updateCollaborationQueueItem,
} from './collaborationQueue';
import { RECONNECT_BACKOFF_MS, RECOVERY_STATES, recoveryStateFor } from './collaborationRecovery';
import { attemptRecoveryMerge, sourceToLineOperations } from './mergeRecovery';
import { uuidv7 } from '../../lib/annotation/annotationId';

export const COLLABORATION_SOCKET_PATH = '/choreo-socket.io';
// M004-F0667 (c): silence (no accepted/cursor/presence/resync frame) after
// which a live, clean session that shares the draft with others re-admits.
export const IDLE_RESYNC_AFTER_MS = 90_000;
const othersPresent = (people = []) => people.length > 1 || people.some((person) => Number(person.clientInstanceCount) > 1);

export const COLLABORATION_EVENTS = {
  join: 'choreography:v1:join-draft', joined: 'choreography:v1:joined-draft', heartbeat: 'choreography:v1:heartbeat',
  presence: 'choreography:v1:presence', cursor: 'choreography:v1:cursor', submit: 'choreography:v1:submit-update',
  accepted: 'choreography:v1:update-accepted', rejected: 'choreography:v1:update-rejected',
  resync: 'choreography:v1:resync-state', rotated: 'choreography:v1:draft-rotated', revoked: 'choreography:v1:authority-revoked',
};

const responseData = (response) => response?.data?.data || response?.data || response;
const safeError = (cause) => ({
  code: cause?.response?.data?.error?.code || cause?.response?.data?.code || cause?.code || 'SERVICE_UNAVAILABLE',
  message: cause?.response?.data?.error?.message || cause?.response?.data?.message || cause?.message || 'Collaboration is unavailable.',
});
const bound = (value, resource) => value?.protocolVersion === COLLABORATION_PROTOCOL
  && String(value.organisationId) === String(resource.organisationId) && value.routineId === resource.routineId && value.draftId === resource.draftId;

function telemetry(name, details = {}) {
  const safe = Object.fromEntries(Object.entries(details).filter(([key]) => !/source|text|payload|token|operation/i.test(key)));
  globalThis.dispatchEvent?.(new CustomEvent('choreo:collaboration:telemetry', { detail: { name, ...safe } }));
}

function visibleDocument(canonical, pending) {
  return pending.reduce((document, item, index) => applyOperation(document, item.operation,
    Number(canonical.serverSequence) + index + 1, 'system', item.kind === 'redo'), canonical);
}

export default function useChoreographyCollaboration({ organisationId, routineId, userId, enabled, onStatus }) {
  const [state, setState] = useState({ phase: 'idle', document: null, pendingCount: 0, pendingOperations: [], people: [], cursors: {}, message: '', recovery: null,
    recoveryText: '', sourceHash: '', protectedOffline: false, encryptionCapableOffline: true,
    // MCRO001-F0148/F0150 (D004 §12.1/§12.3, choreo issue #41): divergence
    // provenance from the bootstrap envelope. PLACEHOLDER nulls until the
    // `joined` payload arrives; an older server simply never populates them
    // and every consumer must degrade to the plain phase label.
    draftUpdatedAt: null, baseRevisionId: null, lastPublishedAt: null,
    // #17 requirement 5(e): protectedOffline/encryptionCapableOffline above
    // are PLACEHOLDER defaults until collaborationQueueProtection() actually
    // resolves; every real UI has consumed this as "protection is currently
    // false" and flashed the "cannot be stored durably" warning for the
    // ~1-2s bootstrap window even when the real, resolved answer was
    // protected — a transient warning during a normal load, not a true one.
    // protectionKnown gates the warning until the real answer is in.
    protectionKnown: false, remoteAnnouncement: '', undoAvailable: false, redoAvailable: false, undoMessage: '',
    viewStateRestore: null });
  const [generation, setGeneration] = useState(0);
  const stateRef = useRef(state); const canonicalRef = useRef(null); const pendingRef = useRef([]); const resourceRef = useRef(null);
  const socketRef = useRef(null); const admittedRef = useRef(false); const pumpingRef = useRef(false); const composingRef = useRef(false); const bufferedRef = useRef([]);
  const phaseRef = useRef('idle'); const clientSequenceRef = useRef(0); const cursorTimerRef = useRef(null); const cursorLatestRef = useRef(null);
  const heartbeatRef = useRef(null); const joinTimeoutRef = useRef(null); const retryRef = useRef({ attempt: 0, timer: null });
  const undoRef = useRef([]); const redoRef = useRef([]); const acceptedLogRef = useRef([]); const announcementRef = useRef({ count: 0, names: new Set(), timer: null });
  // #17: the persisted queue items that failed the join-time rebase-applicability
  // check (visibleDocument threw). Deliberately kept OUT of pendingRef.current —
  // replaying their operations is exactly what throws — but the panel's "Merge
  // with the latest" primary (resync()) needs the list to retry a genuine TEXT
  // merge and to drain durable storage on success. Cleared on discard/unmount.
  const unrebasedQueueRef = useRef([]);
  // Reconnecting-tier failures burn through this budget silently (toast only)
  // before any panel appears; it survives the re-bootstrap each attempt
  // triggers and resets on successful admission (Mantis #1128, D004 §4.1).
  const reconnectBudgetRef = useRef({ attempts: 0 });
  // M004-F0668 (a) (WebUI #21): re-admission on the EXISTING socket, installed
  // by the bootstrap once its socket exists. null = no socket yet.
  const readmitRef = useRef(null);
  // M004-F0667 (b)/(c) (WebUI #18): last inbound collaboration frame, and a
  // latch so one piece of gap evidence starts at most one resync.
  const lastInboundRef = useRef(0); const gapResyncRef = useRef(false);

  useEffect(() => { stateRef.current = state; }, [state]);

  const publishState = useCallback((patch = {}) => {
    const canonical = canonicalRef.current; const pending = pendingRef.current; let document = canonical;
    try { if (canonical) document = visibleDocument(canonical, pending); } catch (cause) {
      patch = { ...patch, phase: 'recovery', recovery: { code: 'ENTITY_CONFLICT', message: cause.message } };
    }
    if (patch.phase) phaseRef.current = patch.phase;
    const history = { undoAvailable: undoRef.current.length > 0, redoAvailable: redoRef.current.length > 0 };
    setState((current) => ({ ...current, document, pendingCount: pending.length,
      pendingOperations: pending.map((item) => item.operation), ...history, ...patch }));
    if (canonical) {
      const score = canvasScore(document || canonical);
      onStatus?.({ draftId: canonical.draftId, serverSequence: canonical.serverSequence, pendingCount: pending.length,
        phase: patch.phase || phaseRef.current, sourceHash: patch.sourceHash || stateRef.current.sourceHash,
        snapshot: { schemaVersion: 1, sections: score.sections, blocks: score.blocks, scenes: [], cues: [], sourceDocument: document || canonical } });
    }
  }, [onStatus]);

  const enterRecovery = useCallback((code, recoveryMessage, extra = {}) => {
    clearTimeout(retryRef.current.timer); retryRef.current.timer = null;
    telemetry('recovery_required', { code, draftId: canonicalRef.current?.draftId });
    const recoveryState = recoveryStateFor(code);
    // T-MCRO-067 (D004 §4.1.4): does a local edit exist that the edits-unsendable
    // panel's premise ("some of YOUR edits weren't saved yet") is about? The
    // pending optimistic queue, the unrebased durable queue, a recovery-carried
    // annotated merge (its conflict boxes ARE the local text — the queue was
    // drained INTO the merge) or recovery-carried captured local text. Published
    // so the recovery-surface gate can suppress the panel when the premise
    // cannot hold, without remapping the internal code (telemetry stays intact).
    const localEdits = Boolean(pendingRef.current.length || unrebasedQueueRef.current.length
      || extra.merge || extra.localText);
    let recoveryText = extra.localText || pendingRef.current.at(-1)?.recoverySource || '';
    try { if (!recoveryText && canonicalRef.current) recoveryText = exactSource(visibleDocument(canonicalRef.current, pendingRef.current)); } catch { /* keep explicit local recovery */ }
    // Reconnecting-tier failures never open a panel while the auto-retry
    // budget (3 attempts, ~2s/4s/8s) lasts: show the polite toast, schedule a
    // full re-bootstrap, and only escalate to the guided panel on exhaustion.
    if (recoveryState === RECOVERY_STATES.RECONNECTING && reconnectBudgetRef.current.attempts < RECONNECT_BACKOFF_MS.length) {
      const attempt = reconnectBudgetRef.current.attempts++;
      telemetry('recovery_auto_retry', { code, attempt: attempt + 1 });
      // M004-F0668 (a): reconnect on the socket this tab already has — a
      // join frame when it is connected, socket.io's own reconnect when it is
      // not. Only with no socket at all is the full REST + socket bootstrap
      // re-run (each re-run is a new handshake plus the boot GETs, the chatter
      // measured in WebUI #21).
      retryRef.current.timer = setTimeout(() => {
        retryRef.current.timer = null;
        if (readmitRef.current?.()) return;
        setGeneration((value) => value + 1);
      }, RECONNECT_BACKOFF_MS[attempt]);
      publishState({ phase: 'reconnecting', message: 'Reconnecting…', recoveryText, recovery: null });
      return;
    }
    // #17 requirement 5(c): a genuinely, currently-degraded local database
    // (collaborationQueue.js self-healed once and gave up, NOT a browser that
    // simply never had IndexedDB — collaborationStorageIncident() distinguishes
    // the two) is the most actionable thing to tell the person about, ahead of
    // whatever specific code triggered this panel — CollaborativeRecovery.js
    // reads this flag first, before staleQueue, when both could apply.
    const storageDegraded = recoveryState === RECOVERY_STATES.UNSENDABLE && !extra.storageDegraded && collaborationStorageIncident();
    // The internal code and engineer message stay on the recovery object for
    // telemetry and tests only; the panel renders exclusively the approved
    // copy for recovery.state (never recovery.message).
    // T-MCRO-066: SECURE_CONTEXT_REQUIRED joins CHOREO_SUBJECT_TOKEN_UNAVAILABLE
    // in the honest readonly tier — the session cannot start, nothing is in
    // conflict, and no conflict panel may render for it (D004 §4.1.3).
    publishState({ phase: ['CHOREO_SUBJECT_TOKEN_UNAVAILABLE', 'SECURE_CONTEXT_REQUIRED'].includes(code) ? 'readonly' : 'recovery', message: recoveryMessage,
      recoveryText, recovery: { code, message: recoveryMessage, state: recoveryState, localEdits, ...extra, ...(storageDegraded ? { storageDegraded: true } : {}) } });
  }, [publishState]);

  const verifyState = useCallback(async (document, hash) => {
    if (!await verifyDocumentHash(document, hash)) throw Object.assign(new Error('Collaborative source hash verification failed.'), { code: 'INVALID_ENVELOPE' });
  }, []);

  const announceRemote = useCallback((accepted) => {
    const actor = accepted?.actor; if (!actor?.displayName) return;
    const bucket = announcementRef.current; bucket.count += 1; bucket.names.add(String(actor.displayName));
    clearTimeout(bucket.timer); bucket.timer = setTimeout(() => {
      const names = [...bucket.names]; const summary = names.length === 1 ? `${bucket.count} ${bucket.count === 1 ? 'edit' : 'edits'} from ${names[0]}`
        : `${bucket.count} edits from ${names.slice(0, 2).join(' and ')}${names.length > 2 ? ' and others' : ''}`;
      bucket.count = 0; bucket.names.clear(); setState((current) => ({ ...current, remoteAnnouncement: summary }));
    }, 1000);
  }, []);

  // MCRO001-F0050 / T-MCRO-050 (D004 §9): before a divergence path lands in the
  // UNSENDABLE panel, attempt a 3-way merge of the client's queued edits against
  // the freshly resynced server document.
  //   base   = the client-held fork point (canonicalRef BEFORE this resync
  //            overwrote it) — the COMMON PATH, no protocol change. `forkBase`
  //            is null on cold reload / rotation, which routes merge3 to its
  //            2-way DEGRADE path (every diff a conflict) rather than blocking.
  //   ours   = the optimistic document (canonical fork point + pending edits).
  //   theirs = the resynced server document.
  // Clean  -> adopt the server document silently, drop the now-merged queue,
  //           return to live with NO panel (the single-editor common case).
  // Conflicts -> serialize the merged document with inline `[!!conflict …-!!]`
  //           boxes and enter the UNSENDABLE recovery state, which renders them
  //           through AnnotationPicker; the panel's discard/export secondaries
  //           remain the never-lose-text safety net.
  // The optional backend `baseDocument` field (proposal §9.3) is a SEPARATE
  // additive follow-up — NOT implemented here; the client-held base covers the
  // common path and the 2-way degrade covers the fallback.
  const recoverWithMerge = useCallback(async (code, recoveryMessage, serverDocument, extra = {}) => {
    const forkPoint = canonicalRef.current;
    // ours: the optimistic document text; theirs: the resynced server text.
    // #17: extra.oursText lets a caller supply a plain TEXT "ours" directly —
    // needed when the local queue cannot be REPLAYED (visibleDocument throws,
    // exactly the condition that put it here) but its last known full text
    // (recoverySource) is still a genuine, mergeable snapshot.
    let ours; let theirs;
    try {
      ours = extra.oursText || exactSource(forkPoint ? visibleDocument(forkPoint, pendingRef.current) : serverDocument);
      theirs = exactSource(serverDocument);
    } catch { enterRecovery(code, recoveryMessage, extra); return false; }
    // Degrade to 2-way when there is no usable fork point (cold reload/rotation):
    // an empty-queue fork point is still a valid base, so only a genuinely absent
    // base (no forkPoint) forces the degrade.
    const forkBase = forkPoint && !extra.noBase ? exactSource(forkPoint) : null;
    const result = attemptRecoveryMerge(forkBase, ours, theirs);
    // #17: drainTargets defaults to the live pending queue (the existing
    // behaviour); a caller merging an un-rebased DURABLE queue that was never
    // adopted into pendingRef.current (extra.oursText path) passes its own item
    // list here so those durable entries are still cleared on a resolved outcome.
    const drainTargets = extra.drainItems || pendingRef.current;
    if (result.clean) {
      // Silent apply: the merged text integrates our non-conflicting edits into
      // the server document. In the single-editor common case base === ours so
      // merged === theirs and this is a plain adopt; when our clean edits were
      // folded in, the server-verified document is still the correct canonical
      // and any residual net edits re-flow through the ordinary queue on the next
      // keystroke. The server hash gates it (no-mock boundary): the adopted
      // canonical is the exact resynced server document whose hash the caller
      // already verified. Drop the merged queue and return to live — no panel.
      canonicalRef.current = serverDocument;
      pendingRef.current = [];
      await Promise.all(drainTargets.map((item) => removeCollaborationQueueItem(resourceRef.current, item.operationId).catch(() => undefined)));
      telemetry('recovery_merge_clean', { code, degraded: result.degraded, draftId: forkPoint?.draftId });
      publishState({ phase: admittedRef.current ? 'live' : 'admitting', sourceHash: extra.sourceHash || stateRef.current.sourceHash, message: '', cursors: {}, recovery: null, recoveryText: '' });
      return true;
    }
    // Conflicts: hand the annotated merge (inline pickers) to the guided panel.
    // Adopt the server document as canonical (the merge base for the resolution)
    // and DROP the live pending queue — every queued edit is now represented in
    // the annotated merge (as conflict boxes or already-merged text), so leaving
    // it in pendingRef would double-apply it when resolveMerge submits. The text
    // itself is preserved in mergedSource/recoveryText (the export safety net) and
    // in durable storage, so nothing is lost.
    canonicalRef.current = serverDocument;
    pendingRef.current = [];
    await Promise.all(drainTargets.map((item) => removeCollaborationQueueItem(resourceRef.current, item.operationId).catch(() => undefined)));
    telemetry('recovery_merge_conflict', { code, count: result.conflicts.length, degraded: result.degraded, draftId: forkPoint?.draftId });
    enterRecovery(code, recoveryMessage, { ...extra, merge: { conflicts: result.conflicts, degraded: result.degraded }, mergedSource: result.annotated, localText: result.annotated });
    return false;
  }, [enterRecovery, publishState]);

  const resync = useCallback(async (afterSequence = canonicalRef.current?.serverSequence || 0) => {
    const resource = resourceRef.current; if (!resource?.draftId) return false;
    publishState({ phase: 'resyncing', message: 'Checking the durable draft…' });
    try {
      const result = responseData(await choreographyApi.resyncCollaboration(resource.organisationId, resource.routineId, resource.draftId, afterSequence));
      if (result.code === 'DRAFT_ROTATED') { enterRecovery('DRAFT_ROTATED', 'This draft was published and rotated. Your local text was not applied to the new draft.', { newDraftId: result.rotatedToDraftId }); return false; }
      if (result.protocolVersion !== COLLABORATION_PROTOCOL || result.draftId !== resource.draftId) throw Object.assign(new Error('Resync identity did not match.'), { code: 'INVALID_ENVELOPE' });
      if (result.compacted && pendingRef.current.length) {
        // Compaction dropped the ops between the fork point and the server head,
        // so operation replay is unsafe — but a 3-way merge against the resynced
        // server document still recovers the queued text (D004 §9). The base is
        // the client-held fork point (canonicalRef, untouched here); when the
        // server sent a fresh document we verify it and merge against it, and
        // when it did not we have no server text to merge and fall through to the
        // panel. `noBase` marks the cold cases where the fork point is not a
        // trustworthy ancestor across compaction, forcing the 2-way degrade.
        if (result.document) {
          await verifyState(result.document, result.sourceHash);
          const serverDocument = { ...result.document, serverSequence: Number(result.serverSequence) };
          return recoverWithMerge('RESYNC_REQUIRED', 'The durable draft was compacted beyond your queued edits. Export or compare them; they were not replayed automatically.',
            serverDocument, { compacted: true, sourceHash: result.sourceHash, noBase: true });
        }
        enterRecovery('RESYNC_REQUIRED', 'The durable draft was compacted beyond your queued edits. Export or compare them; they were not replayed automatically.', { compacted: true });
        return false;
      }
      // #17: a persisted queue that failed the join-time rebase-applicability
      // check (see the `joined` handler) is intentionally kept OUT of
      // pendingRef.current — replaying its operations is exactly what throws —
      // so THIS resync (the panel's "Merge with the latest" primary calls it
      // directly, CollaborativeCanvas.js's onAttemptMerge) is its one retry
      // point. Its last known full text (recoverySource on the newest queued
      // item) stands in for `ours`: a genuine TEXT snapshot, not an operation
      // replay, so attemptRecoveryMerge can 3-way it against the fork point and
      // the freshly resynced server document exactly like the compacted branch
      // above merges a still-queued pending list.
      if (!pendingRef.current.length && unrebasedQueueRef.current.length && result.document) {
        await verifyState(result.document, result.sourceHash);
        const serverDocument = { ...result.document, serverSequence: Number(result.serverSequence) };
        const staleQueue = unrebasedQueueRef.current;
        const merged = await recoverWithMerge('RESYNC_REQUIRED',
          'Your earlier edits could not be replayed automatically onto the latest version. Export or compare them; they were not applied.',
          serverDocument, { sourceHash: result.sourceHash, oursText: staleQueue.at(-1)?.recoverySource || '', staleQueue: true, drainItems: staleQueue });
        if (merged) unrebasedQueueRef.current = [];
        return merged;
      }
      let document = canonicalRef.current;
      if (result.document) document = { ...result.document, serverSequence: Number(result.serverSequence) };
      else for (const accepted of result.operations || []) {
        if (Number(accepted.serverSequence) !== Number(document.serverSequence) + 1) throw Object.assign(new Error('The server update sequence has a gap.'), { code: 'SEQUENCE_GAP' });
        document = applyOperation(document, accepted.operation, Number(accepted.serverSequence), accepted.actor?.userId || 'system', Boolean(accepted.redoOfOperationId));
      }
      await verifyState(document, result.sourceHash); canonicalRef.current = document;
      // Resynced operations bypass the accepted log, so stored remote anchors
      // (and the receipt fold-forward) cannot be transformed across the gap.
      // Drop them; every active remote caret re-announces within the keep-alive
      // interval or on its next relocation event.
      publishState({ phase: admittedRef.current ? 'live' : 'admitting', sourceHash: result.sourceHash, message: '', cursors: {}, recovery: null, recoveryText: '' });
      telemetry('resync_complete', { draftId: resource.draftId, serverSequence: document.serverSequence }); return true;
    } catch (cause) { const failure = safeError(cause); enterRecovery(failure.code, failure.message); return false; }
  }, [enterRecovery, publishState, verifyState]);

  const applyAccepted = useCallback(async (accepted) => {
    const resource = resourceRef.current; const canonical = canonicalRef.current;
    if (!canonical || !bound(accepted, resource)) { if (accepted?.draftId) enterRecovery('INVALID_ENVELOPE', 'A collaborative event failed resource binding.'); return; }
    const sequence = Number(accepted.serverSequence); const ownIndex = pendingRef.current.findIndex((item) => item.operationId === accepted.operationId);
    if (!Number.isSafeInteger(sequence) || sequence < 1) { enterRecovery('INVALID_ENVELOPE', 'A collaborative event had an invalid sequence.'); return; }
    if (sequence <= Number(canonical.serverSequence)) {
      if (ownIndex >= 0) { const [own] = pendingRef.current.splice(ownIndex, 1); await removeCollaborationQueueItem(resource, own.operationId).catch(() => undefined); publishState({ phase: pendingRef.current.length ? 'saving' : 'live' }); }
      return;
    }
    if (sequence !== Number(canonical.serverSequence) + 1) { await resync(canonical.serverSequence); return; }
    const ownCandidate = ownIndex >= 0 ? pendingRef.current[ownIndex] : null;
    if (ownCandidate?.redoOfOperationId && accepted.redoOfOperationId !== ownCandidate.redoOfOperationId) {
      // Local Undo/Redo provenance is genuinely out of sync with what the server
      // accepted; resync cannot repair this client's own chain, so this is one of
      // the unresolvable cases that reaches the panel directly (mirrors the
      // structural-conflict branch below).
      enterRecovery('INVALID_ENVELOPE', 'Redo acknowledgement provenance did not match the submitted Undo chain.'); return;
    }
    const acceptedContext = Boolean(accepted.redoOfOperationId);
    let nextCanonical; let acceptedInverse;
    try {
      // D004 §4.1: applying the canonical transformed operation and verifying its
      // resulting `sourceHash` can fail on a merely stale local view (the same
      // condition `resync-state`/hash divergence self-corrects elsewhere). Attempt
      // `resync()` first and only fall back to the panel if the resync itself
      // fails, rather than treating every divergence here as an unresolvable
      // conflict.
      acceptedInverse = inverseOperations(canonical, accepted.operation, acceptedContext);
      nextCanonical = applyOperation(canonical, accepted.operation, sequence, accepted.actor?.userId || 'system', acceptedContext);
      await verifyState(nextCanonical, accepted.sourceHash);
    } catch (cause) {
      const resynced = await resync(canonical.serverSequence).catch(() => false);
      if (!resynced) enterRecovery(cause.code || 'ENTITY_CONFLICT', cause.message || 'Concurrent edits need comparison.');
      return;
    }
    // Snapshot the queue BEFORE the transform loop mutates it, so the merge
    // fallback below can reconstruct the optimistic `ours` document from the
    // original queued edits against the pre-accept fork point.
    const originalPending = pendingRef.current.map((item) => ({ ...item }));
    try {
      canonicalRef.current = nextCanonical; acceptedLogRef.current.push({ ...accepted, documentBefore: canonical });
      // Mirror the server's presence relocation locally (D004 §5.3): every
      // stored remote anchor behind this accepted sequence transforms through
      // the exact accepted operation with the durable tombstone redirects, so
      // remote carets stay glued to the right text before (and independent of)
      // the server's relocated-cursor event. A vanished line drops the caret.
      setState((current) => {
        const entries = Object.entries(current.cursors);
        if (!entries.length) return current;
        const cursors = {};
        for (const [instanceId, cursor] of entries) {
          if (Number(cursor.anchorServerSequence || 0) >= sequence) { cursors[instanceId] = cursor; continue; }
          let next = null;
          try { next = transformCollaborationAnchor(cursor.anchor, accepted.operation, nextCanonical); } catch { next = null; }
          if (next) cursors[instanceId] = { ...cursor, anchor: next, anchorServerSequence: sequence };
        }
        return { ...current, cursors };
      });
      let viewStateRestore = null;
      if (ownIndex >= 0) {
        const [own] = pendingRef.current.splice(ownIndex, 1); await removeCollaborationQueueItem(resource, own.operationId);
        if (own.kind === 'undo') {
          if (acceptedInverse) redoRef.current.push({ ...own.undoEntry,
            redoOperations: own.undoEntry?.operation?.type === 'line.delete' ? [own.undoEntry.operation] : acceptedInverse,
            redoBaseSequence: sequence, undoOperationId: accepted.operationId });
          else setState((current) => ({ ...current, undoMessage: 'Redo is not safe for this accepted inverse. Compare and reapply instead.' }));
          if (own.undoEntry?.viewStateBefore) viewStateRestore = { ...own.undoEntry.viewStateBefore, kind: 'undo', serverSequence: sequence };
        } else if (acceptedInverse) {
          undoRef.current.push({ operationId: accepted.operationId, operation: accepted.operation, inverseOperations: acceptedInverse, serverSequence: sequence,
            viewStateBefore: own.viewStateBefore || own.redoEntry?.viewStateBefore || null,
            viewStateAfter: own.viewStateAfter || own.redoEntry?.viewStateAfter || null });
          if (own.kind !== 'redo') redoRef.current = [];
          if (own.kind === 'redo' && own.redoEntry?.viewStateAfter) viewStateRestore = { ...own.redoEntry.viewStateAfter, kind: 'redo', serverSequence: sequence };
        } else setState((current) => ({ ...current, undoMessage: 'This accepted edit is not one of the operation kinds the server can cryptographically verify for Undo. Compare and reapply instead.' }));
      } else {
        const before = canonical;
        pendingRef.current = pendingRef.current.map((item) => {
          const operation = transformPendingThroughRemote(item.operation, accepted.operation, item.operationId, before, acceptedContext);
          if (containsServerOnlyCompound(operation)) throw Object.assign(
            new Error('A concurrent insertion intersects a queued deletion. Compare or export the local change; only the server may derive the required compound operation.'),
            { code: 'ENTITY_CONFLICT' },
          );
          return { ...item, operation };
        });
        await Promise.all(pendingRef.current.map((item) => updateCollaborationQueueItem(resource, item))); announceRemote(accepted);
      }
      retryRef.current.attempt = 0;
      publishState({ phase: pendingRef.current.length ? 'saving' : 'live', sourceHash: accepted.sourceHash, message: '',
        ...(viewStateRestore ? { viewStateRestore } : {}) });
    } catch (cause) {
      // A genuine structural conflict (D004 §9): the server document (nextCanonical,
      // now canonical) and the client's queued edits cannot be reconciled by the
      // operation transform. Attempt a 3-way merge before the UNSENDABLE panel —
      // base = the pre-accept fork point `canonical`, ours = the optimistic
      // document (fork + original pending), theirs = the server head `nextCanonical`.
      // Clean merges apply silently; conflicts reach the picker panel. The queue
      // items are still in `originalPending` (durable storage untouched on throw),
      // so nothing is lost either way.
      try {
        const base = exactSource(canonical);
        const ours = exactSource(visibleDocument(canonical, originalPending));
        const theirs = exactSource(nextCanonical);
        const result = attemptRecoveryMerge(base, ours, theirs);
        if (result.clean) {
          canonicalRef.current = nextCanonical;
          pendingRef.current = [];
          await Promise.all(originalPending.map((item) => removeCollaborationQueueItem(resource, item.operationId).catch(() => undefined)));
          telemetry('recovery_merge_clean', { code: 'ENTITY_CONFLICT', degraded: result.degraded, draftId: resource.draftId });
          publishState({ phase: pendingRef.current.length ? 'saving' : 'live', sourceHash: accepted.sourceHash, message: '', recovery: null, recoveryText: '' });
          return;
        }
        // Adopt the server head and drop the pending queue (represented in the
        // annotated merge now); nothing is lost — the text is in mergedSource and
        // durable storage.
        canonicalRef.current = nextCanonical;
        pendingRef.current = [];
        await Promise.all(originalPending.map((item) => removeCollaborationQueueItem(resource, item.operationId).catch(() => undefined)));
        telemetry('recovery_merge_conflict', { code: 'ENTITY_CONFLICT', count: result.conflicts.length, degraded: result.degraded, draftId: resource.draftId });
        enterRecovery(cause.code || 'ENTITY_CONFLICT', cause.message || 'Concurrent edits need comparison.',
          { merge: { conflicts: result.conflicts, degraded: result.degraded }, mergedSource: result.annotated, localText: result.annotated });
        return;
      } catch { enterRecovery(cause.code || 'ENTITY_CONFLICT', cause.message || 'Concurrent edits need comparison.'); }
    }
  }, [announceRemote, enterRecovery, publishState, resync, verifyState]);

  // Accepted events must apply strictly one at a time: the submit acknowledgement
  // and the room broadcast of the same operation (or two remote operations) can
  // otherwise interleave across the awaits inside applyAccepted and apply the
  // same server sequence twice, corrupting the canonical document and failing
  // source-hash verification.
  const acceptedQueueRef = useRef(Promise.resolve());
  const handleAccepted = useCallback((accepted) => {
    const run = acceptedQueueRef.current.then(() => applyAccepted(accepted));
    acceptedQueueRef.current = run.catch(() => undefined);
    return run;
  }, [applyAccepted]);

  const acceptedWithCompositionFence = useCallback((accepted) => {
    if (composingRef.current) { bufferedRef.current.push(accepted); return; } void handleAccepted(accepted);
  }, [handleAccepted]);

  const scheduleRetry = useCallback((work) => {
    if (retryRef.current.timer || phaseRef.current === 'recovery' || phaseRef.current === 'readonly') return;
    if (retryRef.current.attempt >= 10) { publishState({ phase: 'offline', message: 'Automatic retry paused. Reconnect when the network is stable.' }); return; }
    const attempt = retryRef.current.attempt++; const jitter = 0.8 + Math.random() * 0.4;
    const delay = Math.round(Math.min(30_000, 1000 * (2 ** attempt)) * jitter);
    retryRef.current.timer = setTimeout(() => { retryRef.current.timer = null; work(); }, delay);
  }, [publishState]);

  const pump = useCallback(async () => {
    if (pumpingRef.current || !pendingRef.current.length || !canonicalRef.current || !admittedRef.current
      || phaseRef.current === 'recovery' || phaseRef.current === 'readonly') return;
    const socket = socketRef.current;
    // navigator.onLine is a hint, not a truth source (headless WebKit reports
    // offline with a live connection); the admitted socket state decides.
    if (!socket?.connected) { publishState({ phase: 'offline', message: 'Offline · local recovery is available on this device' }); return; }
    pumpingRef.current = true; const item = pendingRef.current[0]; const resource = resourceRef.current;
    publishState({ phase: 'saving', message: 'Saving…' });
    try {
      // socket.io's timeout() prepends its own error argument and the server acknowledges with
      // (null, acknowledgement); take the first substantive reply so the acknowledgement is never
      // mistaken for the server's leading null.
      const accepted = await new Promise((resolve, reject) => socket.timeout(5000).emit(COLLABORATION_EVENTS.submit,
        makeEnvelope(resource, item, canonicalRef.current.serverSequence),
        (error, ...replies) => error ? reject(error) : resolve(responseData(replies.find((reply) => reply != null)))));
      if (accepted?.code && accepted.code !== 'OK') throw Object.assign(new Error(accepted.message || 'Update rejected.'), accepted);
      if (accepted?.operationId) await handleAccepted(accepted); retryRef.current.attempt = 0;
    } catch (cause) {
      const failure = safeError(cause);
      if (['DRAFT_ROTATED', 'AUTHORITY_LOST', 'ENTITY_CONFLICT', 'UNSUPPORTED_PROTOCOL', 'STALE_DRAFT'].includes(failure.code)) enterRecovery(failure.code, failure.message);
      else if (['SEQUENCE_GAP', 'RESYNC_REQUIRED'].includes(failure.code)) await resync(canonicalRef.current.serverSequence);
      else { publishState({ phase: 'offline', message: 'Save needs attention · retrying with backoff' }); scheduleRetry(pump); }
    } finally { pumpingRef.current = false; if (pendingRef.current.length && phaseRef.current === 'saving') scheduleRetry(pump); }
  }, [enterRecovery, handleAccepted, publishState, resync, scheduleRetry]);

  const submitOperations = useCallback(async (operations, metadata = {}) => {
    const canonical = canonicalRef.current; const resource = resourceRef.current;
    if (!canonical || !resource || !admittedRef.current || ['recovery', 'readonly', 'admitting'].includes(phaseRef.current) || !operations.length) return false;
    const operation = operations.length === 1 ? operations[0] : { type: 'transaction.edit', operations };
    const clientSequence = ++clientSequenceRef.current;
    let recoverySource = '';
    try { recoverySource = exactSource(applyOperation(visibleDocument(canonical, pendingRef.current), operation,
      canonical.serverSequence + pendingRef.current.length + 1, 'system', metadata.kind === 'redo')); }
    catch (cause) { enterRecovery(cause.code || 'INVALID_UPDATE', cause.message || 'This change cannot be applied safely.'); return false; }
    const item = { operationId: `${resource.clientInstanceId}:${clientSequence}`, clientSequence,
      authoredBaseServerSequence: canonical.serverSequence, operation, recoverySource, createdAt: Date.now(), ...metadata };
    try { window.localStorage.setItem(`studioaeye_choreo_sequence_v1:${resource.clientInstanceId}:${resource.draftId}`, String(clientSequenceRef.current)); } catch { /* sequence persistence is best effort */ }
    pendingRef.current.push(item); publishState({ phase: 'saving', message: 'Saving…' });
    try { await putCollaborationQueueItem(resource, item); void pump(); return true; }
    catch (cause) { enterRecovery(cause.code || 'OFFLINE_QUEUE_FULL', cause.message || 'Save needs attention.', { quota: true }); return false; }
  }, [enterRecovery, publishState, pump]);

  useEffect(() => {
    if (!enabled || !organisationId || !routineId || !userId) { canonicalRef.current = null; pendingRef.current = []; setState((current) => ({ ...current, phase: 'idle', document: null })); return undefined; }
    let cancelled = false; let admittedOnce = false; const clientInstanceId = getClientInstanceId(userId);
    const bootstrap = async () => {
      publishState({ phase: 'loading', message: 'Opening the durable draft…', recovery: null, people: [], cursors: {}, viewStateRestore: null, protectionKnown: false });
      try {
        const sessionToken = getAccessToken(); let choreoToken = getChoreographySubjectToken();
        // The audience-bound subject token expires after minutes. Every REST choreography call
        // refreshes it through the rotating refresh token; live editing must attempt the same
        // refresh here, otherwise any session older than the token lifetime silently declares
        // itself read-only forever even though admission would succeed.
        if (!choreoToken) { try { choreoToken = await ensureChoreographySubjectToken(); } catch { choreoToken = null; } }
        if (cancelled) return;
        if (!choreoToken || choreoToken === sessionToken) {
          enterRecovery('CHOREO_SUBJECT_TOKEN_UNAVAILABLE', 'Live editing is read-only until the external Core Choreo app-launch supplies its audience-bound subject token.'); return;
        }
        const initial = responseData(await choreographyApi.getCollaboration(organisationId, routineId));
        const document = validateBootstrap(initial, organisationId, routineId); await verifyState(document, initial.sourceHash); if (cancelled) return;
        const resource = { userId: String(userId), organisationId: String(organisationId), routineId, draftId: initial.draftId, clientInstanceId };
        resourceRef.current = resource; canonicalRef.current = document;
        const protection = await collaborationQueueProtection(resource); let queued = await loadCollaborationQueue(resource);
        let storedSequence = 0; try { storedSequence = Number(window.localStorage.getItem(`studioaeye_choreo_sequence_v1:${clientInstanceId}:${initial.draftId}`)) || 0; } catch { /* unavailable */ }
        clientSequenceRef.current = queued.reduce((highest, item) => Math.max(highest, Number(item.clientSequence) || 0), storedSequence);
        if (queued.length) {
          const oldest = Math.min(...queued.map((item) => Number(item.authoredBaseServerSequence ?? document.serverSequence)));
          if (oldest < document.serverSequence) {
            const missing = responseData(await choreographyApi.resyncCollaboration(organisationId, routineId, initial.draftId, oldest));
            // MCRO001-F0050 / T-MCRO-050 (D004 §9, Case J): mirror resync()'s own
            // compaction branch above (~line 189) — a compacted/rotated bootstrap
            // reconciliation must attempt a real 3-way merge against the server's
            // fresh document BEFORE this queue is ever surfaced as the UNSENDABLE
            // panel, exactly like resync() already does mid-session. This was the
            // #12 defect: the bootstrap path was a second, un-synced copy of this
            // logic that jumped straight to enterRecovery and never attempted a
            // merge, so a stuck queue re-fired the same discard-only panel on
            // every page load. DRAFT_ROTATED still goes straight to the panel: the
            // draft identity itself changed, so there is no "theirs" of the SAME
            // draft to merge queued edits against (mirrors resync()'s own
            // top-level DRAFT_ROTATED check). A compacted response with no
            // document at all has nothing to merge against either.
            if (missing.code === 'DRAFT_ROTATED' || (missing.compacted && !missing.document)) {
              pendingRef.current = queued; publishState({ protectedOffline: protection.protected, encryptionCapableOffline: protection.capable, protectionKnown: true });
              enterRecovery(missing.code || 'RESYNC_REQUIRED', 'Queued edits cannot be safely rebased after compaction or rotation. They remain available to export.',
                { compacted: Boolean(missing.compacted), newDraftId: missing.rotatedToDraftId }); return;
            }
            if (missing.compacted && missing.document) {
              await verifyState(missing.document, missing.sourceHash);
              const serverDocument = { ...missing.document, serverSequence: Number(missing.serverSequence) };
              // recoverWithMerge reads canonicalRef.current (already `document`,
              // this bootstrap's freshly verified fork point) and pendingRef.current
              // for the "ours" side, so the durable queue must be in pendingRef
              // before calling it. `noBase: true` forces the 2-way degrade, exactly
              // as resync() always does here: a bootstrap fork point is not a
              // trustworthy 3-way ancestor of a compaction/heal it never observed.
              pendingRef.current = queued;
              publishState({ protectedOffline: protection.protected, encryptionCapableOffline: protection.capable, protectionKnown: true });
              const merged = await recoverWithMerge('RESYNC_REQUIRED',
                'The durable draft was compacted beyond your queued edits. Export or compare them; they were not replayed automatically.',
                serverDocument, { compacted: true, sourceHash: missing.sourceHash, noBase: true });
              // A conflict (or the internal merge-computation fallback) already
              // called enterRecovery and left the durable queue drained; bootstrap
              // stops here either way. A CLEAN merge already adopted the server
              // document into canonicalRef.current and cleared pendingRef.current
              // and durable storage — fall through to the normal admission path
              // below with an empty local `queued` so it is not re-adopted.
              if (!merged) return;
              queued = [];
            }
            if ((missing.operations || []).some((item) => /^(line\.|transaction\.)/.test(item.operation?.type || ''))) {
              pendingRef.current = queued; publishState({ protectedOffline: protection.protected, encryptionCapableOffline: protection.capable, protectionKnown: true });
              enterRecovery('ENTITY_CONFLICT', 'Structural changes occurred while this editor was offline. Compare or export queued text; it was not replayed.'); return;
            }
            for (const accepted of missing.operations || []) queued = queued.map((item) => ({ ...item,
              operation: transformThroughRemote(item.operation, accepted.operation, item.operationId) }));
            if (queued.some((item) => containsServerOnlyCompound(item.operation))) {
              pendingRef.current = queued; publishState({ protectedOffline: protection.protected, encryptionCapableOffline: protection.capable, protectionKnown: true });
              enterRecovery('ENTITY_CONFLICT', 'A concurrent insertion intersects an offline deletion. Compare or export the local change; only the server may derive the required compound operation.'); return;
            }
            await Promise.all(queued.map((item) => updateCollaborationQueueItem(resource, item)));
          }
        }
        // MCRO001-F0050 / T-MCRO-050 (#17): connect and admit the socket
        // INDEPENDENTLY of whether this persisted queue can ultimately be
        // rebased — the final applicability check that used to live here (and
        // returned before `io()` was ever reached when it threw) now runs
        // inside the `joined` handler below, AFTER admission. A queue that
        // fails it still leaves this session LIVE and ADMITTED behind the
        // recovery panel, instead of parking the client with no socket at all
        // (issue #17: the primary panel action, a fresh resync/merge, had
        // nothing to rejoin — the socket had simply never been created).
        publishState({ phase: 'admitting', protectedOffline: protection.protected, encryptionCapableOffline: protection.capable, protectionKnown: true, sourceHash: initial.sourceHash,
          // MCRO001-F0148/F0150: the REST bootstrap carries the same additive
          // divergence provenance as the join frame; capture it at first
          // arrival so the badge is honest even before (or without) admission.
          draftUpdatedAt: initial.draftUpdatedAt || null, baseRevisionId: initial.baseRevisionId || null,
          lastPublishedAt: initial.lastPublishedAt || null,
          message: 'Verifying live editing authority…' });
        const socket = io(config.wsUrl || window.location.origin, { path: COLLABORATION_SOCKET_PATH, transports: ['websocket', 'polling'], reconnection: true,
          reconnectionAttempts: 10, reconnectionDelay: 1000, reconnectionDelayMax: 30_000,
          auth: (callback) => {
            ensureChoreographySubjectToken().then((freshChoreoToken) => callback({ token: getAccessToken(), choreoToken: freshChoreoToken,
              clientInstanceId, supportedProtocolVersions: [COLLABORATION_PROTOCOL] }))
              .catch(() => callback({ token: getAccessToken(), clientInstanceId, supportedProtocolVersions: [COLLABORATION_PROTOCOL] }));
          } });
        socketRef.current = socket;
        // Arm the admission deadline before the transport handshake, not only on 'connect':
        // a websocket that upgrades (101) but whose namespace handshake is then rejected —
        // or whose auth callback stalls — would otherwise leave this state machine parked in
        // 'admitting' forever with a silently read-only editor and zero feedback.
        clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = setTimeout(() => {
          if (!admittedRef.current && !['recovery', 'readonly'].includes(phaseRef.current)) {
            enterRecovery('SERVICE_UNAVAILABLE', 'Live editing admission timed out; the draft remains read-only.');
          }
        }, 15_000);
        socket.on('connect_error', (cause) => {
          if (admittedRef.current || ['recovery', 'readonly'].includes(phaseRef.current)) return;
          publishState({ phase: 'admitting', message: `Live editing authority is unreachable · ${String(cause?.message || 'retrying')}` });
        });
        socket.io?.on?.('reconnect_failed', () => {
          if (!admittedRef.current && !['recovery', 'readonly'].includes(phaseRef.current)) {
            enterRecovery('SERVICE_UNAVAILABLE', 'The live editing connection could not be established; the draft remains read-only.');
          }
        });
        const sendJoin = () => {
          socket.emit(COLLABORATION_EVENTS.join, { protocolVersion: COLLABORATION_PROTOCOL, organisationId: String(organisationId), routineId,
            draftId: initial.draftId, clientInstanceId, lastAcceptedServerSequence: canonicalRef.current?.serverSequence || 0 });
          clearTimeout(joinTimeoutRef.current); joinTimeoutRef.current = setTimeout(() => enterRecovery('SERVICE_UNAVAILABLE', 'Live editing admission timed out; the draft remains read-only.'), 8000);
        };
        // M004-F0668 (a): re-admit on this same socket. Returns false when the
        // socket is gone (cancelled/unmounted) so the caller falls back.
        readmitRef.current = () => {
          if (cancelled || socketRef.current !== socket) return false;
          if (socket.connected) { admittedRef.current = false; publishState({ phase: 'admitting', message: 'Verifying live editing authority…' }); sendJoin(); }
          else socket.connect();
          return true;
        };
        socket.on('connect', () => {
          admittedRef.current = false; publishState({ phase: 'admitting', message: 'Verifying live editing authority…' });
          sendJoin();
        });
        socket.on(COLLABORATION_EVENTS.joined, async (joined) => {
          try {
            if (!bound(joined, resource) || joined.clientInstanceId !== clientInstanceId || !joined.authSessionId) throw Object.assign(new Error('Joined-draft binding was invalid.'), { code: 'INVALID_ENVELOPE' });
            const joinedDocument = validateBootstrap(joined, organisationId, routineId); await verifyState(joinedDocument, joined.sourceHash);
            // M004-F0667 (c): a re-admission keeps the LIVE pending queue; the
            // bootstrap-time `queued` snapshot is only the first admission's.
            const restoring = admittedOnce ? pendingRef.current : queued; admittedOnce = true;
            lastInboundRef.current = Date.now(); gapResyncRef.current = false;
            canonicalRef.current = joinedDocument; admittedRef.current = true; clearTimeout(joinTimeoutRef.current); retryRef.current.attempt = 0;
            reconnectBudgetRef.current.attempts = 0;
            clearInterval(heartbeatRef.current); heartbeatRef.current = setInterval(() => {
              if (socket.connected && admittedRef.current) socket.emit(COLLABORATION_EVENTS.heartbeat, { protocolVersion: COLLABORATION_PROTOCOL,
                organisationId: String(organisationId), routineId, draftId: resource.draftId, clientInstanceId, payload: {} });
              // M004-F0667 (c) idle-eviction watchdog (WebUI #18): the server can
              // drop this socket from the draft room without a word (presence
              // sweep after a stale heartbeat), after which no broadcast ever
              // arrives. While others are present, live and clean, a long
              // silence is answered by one join frame on this same socket: the
              // reply carries the head document and restores room membership.
              if (socket.connected && admittedRef.current && phaseRef.current === 'live' && !pendingRef.current.length
                && !unrebasedQueueRef.current.length && othersPresent(stateRef.current.people)
                && Date.now() - lastInboundRef.current >= IDLE_RESYNC_AFTER_MS) {
                lastInboundRef.current = Date.now(); telemetry('idle_readmit', { draftId: resource.draftId });
                sendJoin();
              }
            }, 20_000);
            // MCRO001-F0050 / T-MCRO-050 (#17): the persisted-queue
            // rebase-applicability check (moved here from before `io()` — see
            // the comment above where the socket is created). It now runs
            // against the JUST-ADMITTED document (the freshest the server has),
            // with a live, connected, admitted session already behind it, so a
            // throw here still leaves the client able to rejoin: enterRecovery
            // never disconnects the socket, and the panel's "Merge with the
            // latest" primary (resync(), stashing unrebasedQueueRef below) is a
            // genuine retry against that same live session, not a dead end.
            // The queued items stay OUT of pendingRef.current either way —
            // replaying them via visibleDocument is exactly what throws — so
            // Export/Discard and the merge retry keep seeing them untouched.
            if (restoring.length) {
              try { visibleDocument(joinedDocument, restoring); } catch {
                unrebasedQueueRef.current = restoring;
                publishState({ protectedOffline: protection.protected, encryptionCapableOffline: protection.capable, protectionKnown: true });
                enterRecovery('RESYNC_REQUIRED', 'Queued edits cannot be safely rebased after compaction or rotation. They remain available to export.',
                  { localText: restoring.at(-1)?.recoverySource || '', staleQueue: true }); return;
              }
            }
            pendingRef.current = restoring;
            // MCRO001-F0148/F0150: the join payload carries the additive
            // divergence provenance (D004 §12.1). A pre-F0148 server omits
            // them; keep the previous values cleared rather than stale.
            publishState({ phase: restoring.length ? 'saving' : 'live', sourceHash: joined.sourceHash,
              draftUpdatedAt: joined.draftUpdatedAt || null, baseRevisionId: joined.baseRevisionId || null,
              lastPublishedAt: joined.lastPublishedAt || null,
              message: restoring.length ? 'Restoring protected changes…' : '', recovery: null, recoveryText: '' }); void pump();
          } catch (cause) { const failure = safeError(cause); enterRecovery(failure.code, failure.message); }
        });
        socket.on(COLLABORATION_EVENTS.accepted, (accepted) => { lastInboundRef.current = Date.now(); return acceptedWithCompositionFence(accepted); });
        socket.on(COLLABORATION_EVENTS.rejected, (rejection) => {
          if (rejection?.resyncRequired && admittedRef.current) void resync(canonicalRef.current?.serverSequence || 0);
          else enterRecovery(rejection?.code || 'INVALID_UPDATE', 'Live editing admission or a collaborative change was rejected.');
        });
        socket.on(COLLABORATION_EVENTS.resync, (result) => { lastInboundRef.current = Date.now(); if (result?.draftId === resource.draftId) void resync(canonicalRef.current?.serverSequence || 0); });
        socket.on(COLLABORATION_EVENTS.presence, (event) => {
          if (!bound(event, resource)) return;
          lastInboundRef.current = Date.now();
          // The presence payload carries no server timestamp (unlike accepted
          // operations' serverSequence), so arrival time is the only signal the
          // client has to age entries out below, the same way cursor relays are
          // stamped `receivedAt` on arrival.
          const people = (event.people || []).map((person) => ({ userId: person.userId, displayName: String(person.displayName || ''), initials: String(person.initials || ''),
            avatarRef: person.avatarRef, clientInstanceCount: Number(person.clientInstanceCount) || 1, connectionState: String(person.connectionState || 'online'), receivedAt: Date.now() }));
          const activeUsers = new Set(people.map((person) => String(person.userId)));
          setState((current) => ({ ...current, people, cursors: Object.fromEntries(Object.entries(current.cursors).filter(([, cursor]) => !cursor.actor?.userId || activeUsers.has(String(cursor.actor.userId)))) }));
        });
        socket.on(COLLABORATION_EVENTS.cursor, (event) => {
          const anchor = event?.payload?.anchor;
          if (!bound(event, resource) || event.clientInstanceId === clientInstanceId || !anchor || anchor.field !== 'source') return;
          lastInboundRef.current = Date.now();
          // Raw cursor relays are validated by the server against its current
          // durable document but carry no serverSequence; only relocation
          // events do. Either way the anchor is expressed no earlier than every
          // accepted operation the server broadcast before it, and socket
          // delivery preserves that order — so cursor processing joins the same
          // serialised queue as accepted events. Reading canonicalRef
          // synchronously here could otherwise time-stamp the anchor behind an
          // accepted event still applying and transform it a second time.
          const run = acceptedQueueRef.current.then(() => {
            const canonical = canonicalRef.current;
            const canonicalSequence = Number(canonical?.serverSequence || 0);
            const eventSequence = Number.isSafeInteger(event.serverSequence) ? Number(event.serverSequence) : canonicalSequence;
            // M004-F0667 (b) (WebUI #18): a relocation stamped AHEAD of this
            // client's document — with every accepted frame already applied
            // and nothing buffered behind a composition — proves broadcasts
            // were missed. Catch up now; do not wait for an accepted frame that
            // may never come. One resync per piece of evidence.
            if (canonical && eventSequence > canonicalSequence && admittedRef.current && phaseRef.current === 'live'
              && !composingRef.current && !bufferedRef.current.length && !gapResyncRef.current) {
              gapResyncRef.current = true; telemetry('sequence_gap_detected', { draftId: resource.draftId, serverSequence: eventSequence });
              void resync(canonicalSequence).finally(() => { gapResyncRef.current = false; });
            }
            let adjusted = { sourceLineId: anchor.sourceLineId, field: 'source', offset: Math.max(0, Number(anchor.offset) || 0),
              ...(Number.isSafeInteger(anchor.selectionEnd) ? { selectionEnd: anchor.selectionEnd } : {}) };
            // A relocation stamped behind this client's canonical document
            // folds forward through the accepted operations it has not seen, so
            // a late relocation can never rewind a caret. The locally-witnessed
            // log must be contiguous over that window (a resynced gap cannot be
            // transformed through): on a hole the caret is dropped and the
            // keep-alive or next relocation re-delivers a current anchor.
            if (canonical && eventSequence < canonicalSequence) {
              try {
                const redirects = collaborationAnchorRedirects(canonical);
                let sequence = eventSequence;
                for (const accepted of acceptedLogRef.current) {
                  const acceptedSequence = Number(accepted.serverSequence);
                  if (!adjusted || acceptedSequence <= sequence) continue;
                  if (acceptedSequence !== sequence + 1) { adjusted = null; break; }
                  adjusted = transformCollaborationAnchorWithRedirects(adjusted, accepted.operation, redirects);
                  sequence = acceptedSequence;
                }
                if (sequence !== canonicalSequence) adjusted = null;
              } catch { adjusted = null; }
            }
            setState((current) => {
              const cursors = { ...current.cursors };
              if (!adjusted) delete cursors[event.clientInstanceId];
              else cursors[event.clientInstanceId] = { ...event, anchor: adjusted,
                anchorServerSequence: Math.max(eventSequence, canonicalSequence), receivedAt: Date.now() };
              return { ...current, cursors };
            });
          });
          acceptedQueueRef.current = run.catch(() => undefined);
          return run;
        });
        socket.on(COLLABORATION_EVENTS.rotated, (event) => {
          if (pendingRef.current.length) enterRecovery('DRAFT_ROTATED', 'This draft was published and rotated. Queued text remains available for recovery.', { newDraftId: event?.newDraftId });
          else { admittedRef.current = false; socket.disconnect(); setGeneration((value) => value + 1); }
        });
        socket.on(COLLABORATION_EVENTS.revoked, (event) => {
          if (!bound(event, resource)) return;
          enterRecovery('AUTHORITY_LOST', 'Your editing authority changed. Local text was not sent.');
        });
        socket.on('disconnect', () => {
          // A dead socket receives no further choreography:v1:presence snapshots,
          // so without this the last-known roster (unlike cursors, which age out
          // on their own) would otherwise render forever if this instance never
          // cleanly reloads — the "phantom presence" defect (issue #13).
          admittedRef.current = false; clearInterval(heartbeatRef.current);
          publishState({ phase: pendingRef.current.length ? 'offline' : 'reconnecting', message: 'Reconnecting…', people: [] });
        });
      } catch (cause) { const failure = safeError(cause); if (!cancelled) enterRecovery(failure.code, failure.message); }
    };
    void bootstrap();
    const online = () => { retryRef.current.attempt = 0; socketRef.current?.connect(); };
    const prune = setInterval(() => setState((current) => ({ ...current,
      // Cursor staleness is legitimate even on a healthy connection (a remote
      // caret genuinely goes quiet), so this stays unconditional.
      cursors: Object.fromEntries(Object.entries(current.cursors)
        .filter(([, cursor]) => Date.now() - Number(cursor.receivedAt || 0) < 60_000)),
      // Presence is different: the server only re-broadcasts on join/leave/
      // reap, never on a timer (D004 §5.3), so pruning `people` unconditionally
      // would age two genuinely-idle, still-connected collaborators out of each
      // other's roster for no reason. Only prune while THIS socket is not
      // connected — the silent-death window between a socket dying and either
      // the disconnect handler firing or the next presence snapshot arriving,
      // which is the actual "phantom presence" gap (issue #13): a stalled tab
      // whose socket.io ping-timeout / disconnect event hasn't fired yet.
      people: socketRef.current?.connected ? current.people
        : current.people.filter((person) => Date.now() - Number(person.receivedAt || 0) < 60_000) })), 20_000);
    window.addEventListener('online', online);
    return () => {
      cancelled = true; readmitRef.current = null; window.removeEventListener('online', online); socketRef.current?.disconnect(); socketRef.current = null; admittedRef.current = false;
      clearInterval(heartbeatRef.current); clearInterval(prune); clearTimeout(joinTimeoutRef.current); clearTimeout(cursorTimerRef.current); clearTimeout(retryRef.current.timer);
      clearTimeout(announcementRef.current.timer); canonicalRef.current = null; pendingRef.current = []; unrebasedQueueRef.current = []; resourceRef.current = null; phaseRef.current = 'idle';
      setState((current) => ({ ...current, phase: 'idle', document: null, people: [], cursors: {}, pendingCount: 0, pendingOperations: [], viewStateRestore: null }));
    };
  }, [acceptedWithCompositionFence, enabled, enterRecovery, generation, organisationId, publishState, pump, resync, routineId, userId, verifyState]);

  const sendCursor = useCallback((anchor) => {
    cursorLatestRef.current = anchor; if (cursorTimerRef.current) return;
    cursorTimerRef.current = setTimeout(() => {
      cursorTimerRef.current = null; const resource = resourceRef.current;
      if (socketRef.current?.connected && admittedRef.current && resource && cursorLatestRef.current) socketRef.current.emit(COLLABORATION_EVENTS.cursor, {
        protocolVersion: COLLABORATION_PROTOCOL, organisationId: resource.organisationId, routineId: resource.routineId,
        draftId: resource.draftId, clientInstanceId: resource.clientInstanceId, payload: { anchor: cursorLatestRef.current },
      });
    }, 100);
  }, []);

  const startComposition = useCallback(() => { composingRef.current = true; }, []);
  const commitComposition = useCallback(async (lineId, originalText, composedText) => {
    let localText = composedText;
    // visibleDocument returns the canonical object itself when nothing is
    // pending; recovery preview mutation must happen on a copy or the composed
    // text is silently pre-applied to the canonical document and then applied
    // again by its own acknowledgement.
    try { const recoveryDocument = JSON.parse(JSON.stringify(visibleDocument(canonicalRef.current, pendingRef.current)));
      const line = recoveryDocument.lines.find((item) => item.sourceLineId === lineId);
      if (line) { line.text = composedText; localText = exactSource(recoveryDocument); } } catch { /* retain composed field */ }
    let operations;
    try { operations = lineEditOperations(lineId, originalText, composedText); } catch (cause) { enterRecovery(cause.code || 'INVALID_UPDATE', cause.message, { localText }); return false; }
    const buffered = bufferedRef.current.splice(0); composingRef.current = false;
    try {
      let document = canonicalRef.current;
      for (const accepted of buffered) {
        operations = operations.map((operation, index) => transformPendingThroughRemote(operation, accepted.operation,
          `composition:${index}`, document, Boolean(accepted.redoOfOperationId)));
        await handleAccepted(accepted); document = canonicalRef.current;
      }
      if (!orderedLines(visibleDocument(canonicalRef.current, pendingRef.current)).some((line) => line.sourceLineId === lineId)
        && operations.some((operation) => operation.lineId === lineId)) throw new Error('The line being composed was deleted remotely.');
      return operations.length ? submitOperations(operations) : true;
    } catch (cause) { enterRecovery('ENTITY_CONFLICT', cause.message || 'Remote edits intersected active composition.', { localText }); return false; }
  }, [enterRecovery, handleAccepted, submitOperations]);

  const undo = useCallback(async () => {
    const entry = undoRef.current.pop(); if (!entry) return;
    try {
      let inverse = { type: 'transaction.inverse', ofOperationIds: [entry.operationId], operations: entry.inverseOperations };
      for (const accepted of acceptedLogRef.current.filter((item) => Number(item.serverSequence) > Number(entry.serverSequence))) {
        inverse = transformPendingThroughRemote(inverse, accepted.operation, `undo:${entry.operationId}`,
          accepted.documentBefore, Boolean(accepted.redoOfOperationId));
      }
      const ok = await submitOperations([inverse], { kind: 'undo', undoEntry: entry });
      if (!ok) undoRef.current.push(entry);
    } catch { undoRef.current.push(entry); publishState({ undoMessage: 'This change cannot be safely undone after later structural edits. Compare and reapply instead.' }); }
  }, [publishState, submitOperations]);

  const redo = useCallback(async () => {
    const entry = redoRef.current.pop(); if (!entry) return;
    try {
      let forward = { type: 'transaction.edit', operations: entry.redoOperations };
      for (const accepted of acceptedLogRef.current.filter((item) => Number(item.serverSequence) > Number(entry.redoBaseSequence))) {
        forward = transformPendingThroughRemote(forward, accepted.operation, `redo:${entry.operationId}`,
          accepted.documentBefore, Boolean(accepted.redoOfOperationId));
      }
      const ok = await submitOperations(forward.operations, { kind: 'redo', redoEntry: entry, redoOfOperationId: entry.undoOperationId });
      if (!ok) redoRef.current.push(entry);
    } catch {
      redoRef.current.push(entry); publishState({ undoMessage: 'Redo is no longer safe after structural edits. Compare and reapply instead.' });
    }
  }, [publishState, submitOperations]);

  const discard = useCallback(async () => {
    if (!resourceRef.current) return; await discardCollaborationQueue(resourceRef.current); pendingRef.current = []; unrebasedQueueRef.current = [];
    publishState({ phase: admittedRef.current ? 'live' : 'readonly', recovery: null, recoveryText: '', message: 'Local recovery changes discarded.' }); if (admittedRef.current) await resync(0);
  }, [publishState, resync]);

  // MCRO001-F0050 / T-MCRO-050 (D004 §9): the user finished resolving every merge
  // conflict in the recovery panel. Reconcile the current canonical document to the
  // finalised merged text with line-level operations and submit them through the
  // ordinary queue (admission, optimistic apply, resync and recovery all apply
  // unchanged), then clear the recovery panel. The prior conflict queue was already
  // dropped when the panel opened, so this is a fresh, resolved submission.
  const resolveMerge = useCallback(async (resolvedText) => {
    const canonical = canonicalRef.current;
    if (!canonical || !admittedRef.current) return false;
    let operations;
    try { operations = sourceToLineOperations(canonical, resolvedText, () => uuidv7()); }
    catch (cause) { enterRecovery(cause.code || 'ENTITY_CONFLICT', cause.message || 'The resolved text could not be applied.', { localText: resolvedText }); return false; }
    // Clear the recovery panel first: the conflict is resolved. An empty operation
    // set (the resolution reproduced the server text exactly) simply returns live.
    publishState({ phase: 'live', recovery: null, recoveryText: '', message: '' });
    if (!operations.length) return true;
    return submitOperations(operations);
  }, [enterRecovery, publishState, submitOperations]);

  const checkpoint = useCallback(async (reason) => {
    if (!canonicalRef.current || pendingRef.current.length || !admittedRef.current) throw new Error('Wait for live admission and every change to be saved before creating a checkpoint.');
    return responseData(await choreographyApi.checkpointCollaboration(organisationId, routineId, canonicalRef.current.draftId,
      { expectedServerSequence: canonicalRef.current.serverSequence, reason }));
  }, [organisationId, routineId]);

  // Surfaces translate gestures against the live optimistic document at event
  // time (not a render closure): rapid keystrokes and the input event browsers
  // fire after a composition commit would otherwise diff a stale projection and
  // duplicate operations.
  const currentDocument = useCallback(() => (canonicalRef.current ? visibleDocument(canonicalRef.current, pendingRef.current) : null), []);

  return useMemo(() => ({ ...state, submitOperations, sendCursor, startComposition, commitComposition, undo, redo, discard, resolveMerge, checkpoint, currentDocument,
    exactText: state.document ? exactSource(state.document) : '', clientInstanceId: resourceRef.current?.clientInstanceId,
    retry: () => { retryRef.current.attempt = 0; setGeneration((value) => value + 1); },
    // "Try again now" on the escalated reconnecting panel: retry immediately
    // WITHOUT refunding the auto-retry budget, so another failure returns to
    // the panel at once instead of hiding behind a fresh silent cycle.
    retryNow: () => { retryRef.current.attempt = 0; setGeneration((value) => value + 1); },
    // MCRO001-F0050 / T-MCRO-050 (D004 §9, Case J): the UNSENDABLE panel's
    // "Merge with the latest" primary (CollaborativeRecovery.js) calls this —
    // a fresh resync, which (per the compaction branch above and the mirrored
    // bootstrap branch) attempts a real 3-way merge before any panel would
    // reopen. A clean merge returns to live silently; a conflict repopulates
    // recovery.merge so the panel switches to the inline picker; the
    // export/clipboard/discard secondaries remain the never-lose-text safety
    // net regardless of the outcome.
    resync }),
  [checkpoint, commitComposition, currentDocument, discard, redo, resolveMerge, resync, sendCursor, state, submitOperations, undo]);
}
